// @ts-nocheck
import { YStack, Text, Paragraph } from 'tamagui';
import { Container } from '../components/Container';
import { PageLayout } from '../components/PageLayout';

function Section({ title, children }) {
  return (
    <YStack gap="$3">
      <Text fontSize="$6" fontWeight="600" color="$color12">
        {title}
      </Text>
      {children}
    </YStack>
  );
}

function P({ children }) {
  return (
    <Paragraph fontSize="$4" color="$color8" lineHeight={26}>
      {children}
    </Paragraph>
  );
}

function BulletList({ items }) {
  return (
    <YStack gap="$2" paddingLeft="$4">
      {items.map((item, i) => (
        <Paragraph key={i} fontSize="$4" color="$color8" lineHeight={26}>
          {'\u2022'} {item}
        </Paragraph>
      ))}
    </YStack>
  );
}

export default function TermsPage({ theme, toggleTheme }) {
  return (
    <PageLayout theme={theme} toggleTheme={toggleTheme}>
      <Container>
        <YStack paddingVertical="$8" gap="$8" maxWidth={700}>
          {/* Header */}
          <YStack gap="$4">
            <Text fontSize="$2" color="$color6" textTransform="uppercase" letterSpacing={3} fontWeight="500">
              Legal
            </Text>
            <Text fontSize={40} fontWeight="700" color="$color12" lineHeight={48}>
              Terms of Service
            </Text>
            <Text fontSize="$3" color="$color7">
              Effective date: March 2026
            </Text>
          </YStack>

          <P>
            These Terms of Service ("Terms") govern your use of the products and services provided
            by SID Technologies ("we", "us", "our"), including Torch, Statio, Denarius, JanusLedger,
            Pilum, and Scuta. By using our products, you agree to these Terms.
          </P>

          <Section title="1. Service Description">
            <P>
              SID Technologies provides a suite of software products built on a shared platform
              (platform-core) that handles authentication, billing, organization management, and
              permissions. Individual products include:
            </P>
            <BulletList items={[
              'Torch — Cross-ecosystem search and intranet platform',
              'Statio — Security-first managed MCP platform for AI agents',
              'Denarius — Financial data extraction and portfolio analytics',
              'JanusLedger — Double-entry accounting infrastructure',
              'Pilum — Open-source multi-cloud deployment CLI',
              'Scuta — Managed CLI distribution platform',
            ]} />
            <P>
              Each product may have additional terms specific to its functionality. Where
              product-specific terms conflict with these general Terms, the product-specific
              terms prevail.
            </P>
          </Section>

          <Section title="2. Account Responsibilities">
            <P>
              You are responsible for maintaining the confidentiality of your account credentials
              and for all activity that occurs under your account. You agree to:
            </P>
            <BulletList items={[
              'Provide accurate and complete information when creating an account',
              'Keep your login credentials secure and confidential',
              'Notify us immediately of any unauthorized access to your account',
              'Not share your account with others or create accounts on behalf of others without authorization',
            ]} />
          </Section>

          <Section title="3. Acceptable Use">
            <P>You agree not to use our products to:</P>
            <BulletList items={[
              'Violate any applicable laws or regulations',
              'Infringe on the intellectual property rights of others',
              'Transmit malware, viruses, or other harmful code',
              'Attempt to gain unauthorized access to our systems or other users\' accounts',
              'Interfere with or disrupt the integrity or performance of our services',
              'Use our services for any illegal, fraudulent, or abusive purpose',
              'Reverse engineer, decompile, or disassemble our proprietary software',
              'Resell or redistribute our services without written permission',
            ]} />
          </Section>

          <Section title="4. Payment Terms">
            <P>
              Paid subscriptions are billed through Stripe. By subscribing to a paid plan, you agree
              to the following:
            </P>
            <BulletList items={[
              'Subscriptions are billed monthly or annually, depending on the plan selected',
              'Annual plans include a discount (2 months free) and are billed upfront',
              'Free tiers are available for all products with no payment required',
              'Prices may change with 30 days\' notice; existing subscriptions are honored until renewal',
              'Refunds are handled on a case-by-case basis; contact us within 14 days of a charge',
              'Multi-product discounts (10% for 2 products, 15% for 3, 20% for 4) are applied automatically',
              'Failed payments may result in service suspension after a reasonable grace period',
            ]} />
          </Section>

          <Section title="5. Intellectual Property">
            <P>
              All proprietary software, documentation, trademarks, and content provided by SID
              Technologies remain our exclusive property. Your use of our products does not grant
              you ownership of any intellectual property.
            </P>
            <P>
              You retain ownership of all data and content you create, upload, or store within our
              products. You grant us a limited license to process and store your content solely for
              the purpose of providing our services to you.
            </P>
          </Section>

          <Section title="6. Open Source">
            <P>
              Pilum is licensed under the Business Source License 1.1 (BSL 1.1), which converts to
              Apache 2.0 in December 2028. Scuta is distributed under an open-source license. Use of
              these projects is governed by their respective licenses, available in each project's
              repository.
            </P>
            <P>
              Open-source components used within our proprietary products are subject to their
              respective licenses. A list of open-source dependencies is available upon request.
            </P>
          </Section>

          <Section title="7. Limitation of Liability">
            <P>
              To the maximum extent permitted by applicable law, SID Technologies and its founder,
              employees, and affiliates shall not be liable for any indirect, incidental, special,
              consequential, or punitive damages, including but not limited to loss of profits, data,
              or business opportunities, arising from your use of our products.
            </P>
            <P>
              Our total liability for any claim arising from these Terms or your use of our products
              is limited to the amount you paid us in the twelve (12) months preceding the claim.
            </P>
            <P>
              Our products are provided "as is" and "as available" without warranties of any kind,
              either express or implied, including but not limited to implied warranties of
              merchantability, fitness for a particular purpose, and non-infringement.
            </P>
          </Section>

          <Section title="8. Termination">
            <P>
              You may cancel your account at any time. Upon cancellation:
            </P>
            <BulletList items={[
              'Access to paid features ends at the end of the current billing period',
              'You may export your data before cancellation',
              'Your data will be deleted in accordance with our Privacy Policy',
            ]} />
            <P>
              We may suspend or terminate your account if you violate these Terms, with notice
              where practicable. In cases of severe abuse, we may terminate without prior notice.
            </P>
          </Section>

          <Section title="9. Changes to Terms">
            <P>
              We may update these Terms from time to time. Material changes will be communicated
              via email or through our products. Continued use of our products after changes take
              effect constitutes acceptance of the updated Terms. If you do not agree to the
              updated Terms, you should discontinue use of our products.
            </P>
          </Section>

          <Section title="10. Governing Law">
            <P>
              These Terms are governed by and construed in accordance with the laws of the State
              of Delaware, without regard to its conflict of law provisions.
            </P>
          </Section>

          <Section title="11. Contact">
            <P>
              For questions about these Terms, contact us at{' '}
              <Text
                color="$amber7" cursor="pointer"
                hoverStyle={{ opacity: 0.7 }}
                onPress={() => window.open('mailto:dans@romans.dev')}
              >
                dans@romans.dev
              </Text>
              .
            </P>
          </Section>
        </YStack>
      </Container>
    </PageLayout>
  );
}
